<template>
  <div class="hls-player" :style="{ height }">
    <video
      ref="media"
      controls
      playsinline
      :poster="cover"
      :autoplay="autoplay"
      :preload="preload ? 'auto' : 'metadata'"
      @loadedmetadata="onLoadedMetadata"
      @canplaythrough="$emit('canplaythrough')"
      @play="$emit('play')"
      @pause="$emit('pause')"
      @ended="$emit('ended')"
      @error="onMediaError"
    ></video>
    <div v-if="errorMessage" class="playback-error" role="alert">
      <span>{{ errorMessage }}</span>
      <button type="button" @click="reloadPlayer">重试</button>
    </div>
  </div>
</template>

<script>
import Hls from 'hls.js'

export default {
  name: 'HlsPlayer',
  inheritAttrs: false,
  props: {
    source: { type: String, default: '' },
    cover: { type: String, default: '' },
    height: { type: String, default: '320px' },
    autoplay: { type: Boolean, default: true },
    preload: { type: Boolean, default: false }
  },
  data() {
    return { errorMessage: '', loadSequence: 0 }
  },
  watch: {
    source: 'reloadPlayer'
  },
  mounted() {
    this.reloadPlayer()
  },
  beforeDestroy() {
    this._playerDestroyed = true
    this.loadSequence++
    this.destroyMedia()
  },
  methods: {
    destroyMedia() {
      const hls = this._hls
      this._hls = null
      this._fallbackState = null
      if (hls) hls.destroy()
      const media = this.$refs.media
      if (media) {
        media.pause()
        media.removeAttribute('src')
        media.load()
      }
    },
    reloadPlayer() {
      const sequence = ++this.loadSequence
      return this.$nextTick(() => {
        if (this._playerDestroyed || sequence !== this.loadSequence) return
        this.destroyMedia()
        this._usingNative = false
        this._playRequested = false
        this.errorMessage = ''
        if (!this.source) return
        const media = this.$refs.media
        // Prefer MSE even when Chromium claims native HLS support.
        if (Hls.isSupported()) {
          const hls = new Hls()
          this._hls = hls
          hls.on(Hls.Events.ERROR, (event, data) => {
            // Recoverable errors are retried by hls.js within its retry limits.
            if (this._hls !== hls || !data.fatal) return
            if (data.type === Hls.ErrorTypes.MEDIA_ERROR && this.tryNativePlayback()) return
            this.reportError({ code: data.type, message: data.details })
            this._hls = null
            hls.destroy()
          })
          hls.loadSource(this.source)
          hls.attachMedia(media)
        } else if (media.canPlayType('application/vnd.apple.mpegurl')) {
          this._usingNative = true
          media.src = this.source
        } else {
          this.reportError({ code: 'HLS_UNSUPPORTED', message: '当前浏览器不支持此视频，请使用新版浏览器' })
        }
      })
    },
    tryNativePlayback() {
      const media = this.$refs.media
      if (this._usingNative || this._playerDestroyed || !this.source ||
          !media || !media.canPlayType('application/vnd.apple.mpegurl')) return false
      const state = {
        time: media.currentTime,
        play: this.autoplay || this._playRequested || !media.paused
      }
      // Some streams cannot be transmuxed by MSE but work in the native HLS pipeline.
      // Try that pipeline only once per load, without switching back on failure.
      this._usingNative = true
      this.destroyMedia()
      this._fallbackState = state
      this.errorMessage = ''
      media.src = this.source
      media.load()
      return true
    },
    onLoadedMetadata() {
      const state = this._fallbackState
      this._fallbackState = null
      if (state) {
        if (state.time > 0 && state.time < this.getDuration()) this.seek(state.time)
        if (state.play) this.play()
      }
      this.$emit('canplaythrough')
    },
    reportError(detail) {
      this.errorMessage = '视频播放失败，请重试或刷新页面'
      this.$emit('error', detail)
    },
    onMediaError() {
      const media = this.$refs.media
      if (this._playerDestroyed || !this.source || !media || !media.error) return
      if (this._hls && (media.error.code === 3 || media.error.code === 4) && this.tryNativePlayback()) return
      this.reportError({ code: media.error.code, message: media.error.message })
    },
    play() {
      const media = this.$refs.media
      if (!media) return
      this._playRequested = true
      const sequence = this.loadSequence
      const hls = this._hls
      const playing = media.play()
      if (playing && playing.catch) {
        playing.catch(error => {
          if (this._playerDestroyed || sequence !== this.loadSequence || hls !== this._hls) return
          if (error.name !== 'NotAllowedError' && error.name !== 'AbortError') this.reportError(error)
        })
      }
    },
    pause() {
      this._playRequested = false
      if (this.$refs.media) this.$refs.media.pause()
    },
    seek(time) {
      if (this.$refs.media) this.$refs.media.currentTime = time
    },
    getCurrentTime() {
      return this.$refs.media ? this.$refs.media.currentTime : 0
    },
    getDuration() {
      return this.$refs.media ? this.$refs.media.duration : 0
    }
  }
}
</script>

<style scoped>
.hls-player {
  position: relative;
  width: 100%;
  background: #000;
}
video {
  display: block;
  width: 100%;
  height: 100%;
}
.playback-error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  color: #fff;
  background: rgba(0, 0, 0, 0.8);
}
.playback-error button {
  padding: 6px 18px;
  cursor: pointer;
}
</style>
